import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Play, Pause, RotateCcw, Coffee, Brain, BarChart3, Clock, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const btn3D = "shadow-[0_6px_0_rgba(0,0,0,0.12),0_8px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_0_rgba(0,0,0,0.12),0_6px_12px_rgba(0,0,0,0.06)] hover:translate-y-[2px] active:shadow-[0_1px_0_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.04)] active:translate-y-[5px] transition-all duration-150 ease-out";
const STORAGE_KEY = "ycps_study_timer_sessions";

const PRESETS = [
  { label: "Pomodoro", focus: 25, break: 5, icon: Brain, color: "bg-red-500" },
  { label: "Long Focus", focus: 50, break: 10, icon: Clock, color: "bg-blue-500" },
  { label: "Quick Review", focus: 15, break: 3, icon: Coffee, color: "bg-green-500" },
];

const getSessions = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
  catch { return []; }
};

const StudyTimerPage = () => {
  const navigate = useNavigate();
  const [preset, setPreset] = useState(0);
  const [isBreak, setIsBreak] = useState(false);
  const [timeLeft, setTimeLeft] = useState(PRESETS[0].focus * 60);
  const [running, setRunning] = useState(false);
  const [sessions, setSessions] = useState(getSessions);
  const intervalRef = useRef(null);

  const currentPreset = PRESETS[preset];
  const totalSeconds = (isBreak ? currentPreset.break : currentPreset.focus) * 60;
  const progress = ((totalSeconds - timeLeft) / totalSeconds) * 100;

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  const saveSession = useCallback((mins) => {
    const session = { date: new Date().toISOString(), minutes: mins, preset: currentPreset.label };
    const updated = [session, ...sessions].slice(0, 50);
    setSessions(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  }, [sessions, currentPreset.label]);

  useEffect(() => {
    if (running && timeLeft > 0) {
      intervalRef.current = setInterval(() => setTimeLeft(t => t - 1), 1000);
    } else if (timeLeft === 0 && running) {
      setRunning(false);
      if (!isBreak) {
        saveSession(currentPreset.focus);
        setIsBreak(true);
        setTimeLeft(currentPreset.break * 60);
      } else {
        setIsBreak(false);
        setTimeLeft(currentPreset.focus * 60);
      }
    }
    return () => clearInterval(intervalRef.current);
  }, [running, timeLeft, isBreak, currentPreset, saveSession]);

  const selectPreset = (idx) => {
    setPreset(idx);
    setRunning(false);
    setIsBreak(false);
    setTimeLeft(PRESETS[idx].focus * 60);
  };

  const reset = () => {
    setRunning(false);
    setIsBreak(false);
    setTimeLeft(currentPreset.focus * 60);
  };

  const todayMins = sessions.filter(s => new Date(s.date).toDateString() === new Date().toDateString()).reduce((a, s) => a + s.minutes, 0);
  const weekMins = sessions.filter(s => {
    const d = new Date(s.date);
    const now = new Date();
    const diff = (now - d) / (1000 * 60 * 60 * 24);
    return diff <= 7;
  }).reduce((a, s) => a + s.minutes, 0);

  const radius = 120;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="study-timer-page">
      <div className={`${isBreak ? "bg-gradient-to-r from-green-700 to-emerald-800" : "bg-gradient-to-r from-[#002147] to-[#0a3d7a]"} text-white py-8 transition-colors duration-500`}>
        <div className="max-w-5xl mx-auto px-4">
          <button onClick={() => navigate(-1)} className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-medium rounded-xl mb-4 border border-white/20 ${btn3D} cursor-pointer select-none`} data-testid="back-btn">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-3">
            <Brain size={28} />
            <div>
              <h1 className="text-2xl font-bold">Study Timer</h1>
              <p className="text-white/70 text-sm">Stay focused, track your study sessions</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Preset Selector */}
        <div className="flex justify-center gap-3" data-testid="preset-selector">
          {PRESETS.map((p, i) => (
            <button key={i} onClick={() => selectPreset(i)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium flex items-center gap-1.5 ${preset === i ? "bg-[#002147] text-white" : "bg-white/80 text-[#002147]"} ${btn3D}`}
              data-testid={`preset-${i}`}>
              <p.icon size={14} /> {p.label} ({p.focus}m)
            </button>
          ))}
        </div>

        {/* Timer Circle */}
        <div className="flex justify-center">
          <Card className="border-0 shadow-2xl bg-white/90 backdrop-blur-sm rounded-3xl" data-testid="timer-card">
            <CardContent className="p-8 flex flex-col items-center">
              <Badge className={`mb-4 ${isBreak ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                {isBreak ? "Break Time" : "Focus Mode"}
              </Badge>

              <div className="relative w-[280px] h-[280px] flex items-center justify-center">
                <svg className="absolute inset-0 -rotate-90" viewBox="0 0 280 280">
                  <circle cx="140" cy="140" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="8" />
                  <circle cx="140" cy="140" r={radius} fill="none"
                    stroke={isBreak ? "#10b981" : "#002147"}
                    strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    className="transition-all duration-1000"
                  />
                </svg>
                <div className="text-center z-10">
                  <p className="text-5xl font-bold text-[#002147] tabular-nums" data-testid="timer-display">{formatTime(timeLeft)}</p>
                  <p className="text-sm text-gray-400 mt-1">{isBreak ? "Relax & Recharge" : currentPreset.label}</p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => setRunning(!running)}
                  className={`w-14 h-14 rounded-full flex items-center justify-center text-white ${running ? "bg-orange-500" : "bg-green-600"} ${btn3D}`}
                  data-testid="play-pause-btn">
                  {running ? <Pause size={24} /> : <Play size={24} className="ml-0.5" />}
                </button>
                <button onClick={reset}
                  className={`w-14 h-14 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center ${btn3D}`}
                  data-testid="reset-btn">
                  <RotateCcw size={20} />
                </button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#002147]">{todayMins}</p><p className="text-xs text-gray-500">Minutes Today</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#002147]">{weekMins}</p><p className="text-xs text-gray-500">Minutes This Week</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#002147]">{sessions.length}</p><p className="text-xs text-gray-500">Total Sessions</p></CardContent></Card>
        </div>

        {/* Recent Sessions */}
        {sessions.length > 0 && (
          <Card>
            <CardHeader className="bg-[#002147] text-white rounded-t-lg py-3">
              <CardTitle className="text-lg flex items-center gap-2"><BarChart3 size={18} /> Recent Sessions</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {sessions.slice(0, 8).map((s, i) => (
                <div key={i} className={`flex items-center justify-between px-5 py-3 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"} ${i < Math.min(sessions.length, 8) - 1 ? "border-b" : ""}`}>
                  <div>
                    <p className="text-sm font-semibold text-[#002147]">{s.preset}</p>
                    <p className="text-xs text-gray-400">{new Date(s.date).toLocaleString()}</p>
                  </div>
                  <Badge variant="outline">{s.minutes} min</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default StudyTimerPage;
